import { Injectable } from '@angular/core';

import { Observable, Subject, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';

import { CacheData } from '@models/utils/cache-data';

@Injectable({
    providedIn: 'root'
})
export class CacheService {
    private readonly SECONDS_VALID = 60;

    private readonly cache: Map<string, CacheData<any>> = new Map<string, CacheData<any>>();

    get<T>(key: string, fallback?: Observable<T>, seconds?: number): Observable<T> | Subject<T> {
        if (this.has(key)) {
            return new Observable(a => {
                a.next(this.cache.get(key)?.value);
            });
        } else if (fallback) {
            return fallback.pipe(tap((value) => { this.set<T>(key, value, seconds ?? this.SECONDS_VALID); }));
        } else {
            return throwError('No fallback');
        }
    }

    set<T>(key: string, value: T, seconds: number): void {
        const validTime = new Date();
        validTime.setSeconds(validTime.getSeconds() + seconds);

        this.cache.set(key, { validTime, value });
    }

    remove(key: string): void {
        this.cache.delete(key);
    }

    clear(): void {
        this.cache.clear();
    }

    valid(key: string): boolean {
        const validDate = this.cache.get(key)?.validTime;

        return validDate == null ? false : validDate >= new Date();
    }

    has(key: string): boolean {
        return this.cache.has(key) && this.valid(key);
    }
}
